<template>
  <v-dialog v-model="innerShowValue" persistent scrollable max-width="600">
    <v-card>
      <v-card-title class="primary white--text">
        <v-icon color="white" class="mr-2">mdi-home-map-marker</v-icon>
        {{ detail?.id ? 'Ubah' : 'Tambah' }} Wilayah
        <v-btn class="mt-n1" icon dark top right absolute @click="handleClose">
          <v-icon color="grey">mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text class="pt-6">
        <ValidationObserver ref="observer">
          <v-form ref="form" :readonly="status === 'pending'">
            <p class="subtitle-2 mb-3 text--secondary">
              Informasi Wilayah
            </p>
            <ValidationProvider
              name="Nama"
              rules="required"
              v-slot="{ errors }"
            >
              <v-text-field
                dense
                outlined
                label="Nama"
                color="light-green"
                v-model="form.name"
                :error-messages="formErrors.name || errors"
              >
              </v-text-field>
            </ValidationProvider>
            <p class="subtitle-2 mb-3 text--secondary">
              Lokasi Wilayah
            </p>
            <ValidationProvider
              name="Pilih Provinsi"
              rules="required"
              v-slot="{ errors }"
            >
              <v-autocomplete
                v-model="form.province"
                :error-messages="errors"
                :loading="loadProvinces"
                :items="provinces"
                :search-input.sync="searchProvinces"
                cache-items
                dense
                outlined
                color="light-green"
                label="Pilih Provinsi"
                item-text="name"
                return-object
              />
            </ValidationProvider>
            <ValidationProvider
              name="Pilih Kota"
              rules="required"
              v-slot="{ errors }"
            >
              <v-autocomplete
                v-model="form.city"
                :error-messages="errors"
                :loading="loadCities"
                :items="cities"
                :search-input.sync="searchCities"
                dense
                outlined
                color="light-green"
                label="Pilih Kota"
                item-text="name"
                return-object
              />
            </ValidationProvider>
            <ValidationProvider
              name="Pilih Kecamatan"
              rules="required"
              v-slot="{ errors }"
            >
              <v-autocomplete
                v-model="form.subdistrict"
                :error-messages="errors"
                :loading="loadSubdistricts"
                :items="subdistricts"
                :search-input.sync="searchSubdistricts"
                dense
                outlined
                color="light-green"
                label="Pilih Kecamatan"
                item-text="name"
                return-object
              />
            </ValidationProvider>
            <ValidationProvider
              name="Pilih Kelurahan"
              rules="required"
              v-slot="{ errors }"
            >
              <v-autocomplete
                v-model="form.district"
                :error-messages="formErrors.villageId || errors"
                :loading="loadVillages"
                :items="villages"
                :search-input.sync="searchVillages"
                dense
                outlined
                color="light-green"
                label="Pilih Kelurahan"
                item-text="name"
                return-object
              />
            </ValidationProvider>
            <v-row no-gutters align="start" class="mb-7">
              <v-col
                cols="8"
                style="min-width: 100px;"
                class="flex-grow-0 flex-shrink-1"
              >
                <div
                  class="v-input v-input--dense theme--light v-text-field v-text-field--is-booted v-text-field--enclosed v-text-field--outlined d-block"
                  :class="{
                    'error--text v-input--has-state':
                      formErrors.address || error.address
                  }"
                >
                  <div
                    class="v-input__control"
                    style="height: 40px;border-radius: 4px 0 0 4px;"
                  >
                    <div class="v-input__slot">
                      <fieldset aria-hidden="true" style="height: 40px;top: 0;">
                        <legend style="width: 0px;"></legend>
                      </fieldset>
                      <div class="v-text-field__slot">
                        <label
                          for="input-147"
                          class="v-label theme--light error--text"
                          style="left: 0px; right: auto; position: absolute;"
                        >
                        </label>
                        <gmap-autocomplete
                          placeholder="Alamat lengkap"
                          :value="form.address"
                          :select-first-on-enter="true"
                          @change="validateAddress"
                          @blur="validateAddress"
                          @place_changed="setPlace"
                        />
                      </div>
                    </div>
                  </div>
                  <div v-if="error.address" class="v-text-field__details mt-1">
                    <div
                      class="v-messages theme--light error--text"
                      role="alert"
                    >
                      <div class="v-messages__wrapper">
                        <div class="v-messages__message">
                          {{
                            formErrors.address
                              ? formErrors.address
                              : 'Kolom tidak boleh kosong'
                          }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </v-col>
              <v-col cols="4" class="flex-shrink-0">
                <v-btn
                  style="border-radius: 0px 4px 4px 0px;"
                  elevation="0"
                  height="40"
                  block
                  color="grey darken-1"
                  class="text-capitalize white--text"
                  @click="dialog.map = true"
                >
                  Pilih
                  {{ $vuetify.breakpoint.name !== 'xs' ? 'Dari Peta' : '' }}
                </v-btn>
              </v-col>
            </v-row>
            <div class="d-flex mb-5 justify-space-between align-center">
              <p class="subtitle-2 mb-3 text--secondary">
                Detail Wilayah
              </p>
              <v-btn @click="addNewAttributes" elevation="0" small depressed>
                <v-icon small color="grey">
                  mdi-plus-circle
                </v-icon>
                Tambah
              </v-btn>
            </div>
            <template v-if="form.attributes.length > 0">
              <v-row
                dense
                v-for="(attribute, index) in form.attributes"
                :key="index"
              >
                <v-col cols="5">
                  <ValidationProvider
                    :name="`Label ${index + 1}`"
                    rules="required"
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      v-model="attribute.attributeKey"
                      :error-messages="errors"
                      dense
                      outlined
                      color="light-green"
                      label="Label"
                    />
                  </ValidationProvider>
                </v-col>
                <v-col cols="5">
                  <ValidationProvider
                    :name="`Nilai ${index + 1}`"
                    rules="required"
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      v-model="attribute.attributeValue"
                      :error-messages="errors"
                      dense
                      outlined
                      color="light-green"
                      label="Nilai"
                    />
                  </ValidationProvider>
                </v-col>
                <v-col cols="2" class="d-flex justify-center">
                  <v-btn
                    color="red"
                    dark
                    block
                    elevation="0"
                    @click="removeAttributes(index)"
                  >
                    <v-icon small>
                      mdi-delete
                    </v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </template>
            <template v-else>
              <p class="subtitle-2 text-center mb-3 text--secondary">
                Tidak ada attribute
              </p>
            </template>
          </v-form>
        </ValidationObserver>
        <v-alert
          v-if="errorMessage"
          text
          type="error"
          class="custom-alert my-4"
        >
          <p class="body-2 mb-0">
            {{ errorMessage }}
          </p>
        </v-alert>
      </v-card-text>
      <v-card-actions class="pb-6 px-6">
        <v-spacer></v-spacer>
        <v-btn
          small
          color="#fff"
          elevation="0"
          :disabled="status === 'pending'"
          class="text-capitalize grey--text text--darken-1"
          @click="handleClose"
        >
          Batal
        </v-btn>
        <v-btn
          small
          light
          color="light-green"
          elevation="0"
          :disabled="status === 'pending'"
          :loading="status === 'pending'"
          @click="handleSubmit"
          class="white--text text-capitalize"
        >
          Simpan
        </v-btn>
      </v-card-actions>
    </v-card>
    <DialogSelectLocation v-model="dialog.map" @save="setLocationFromMap" />
  </v-dialog>
</template>

<script>
import axios from 'axios';

import geoMixin from '@/mixins/geo';
import DialogSelectLocation from './DialogSelectLocation.vue';

export default {
  props: {
    value: {
      type: Boolean,
      default: false
    },
    detail: {
      type: Object,
      default: () => {}
    }
  },

  components: {
    DialogSelectLocation
  },

  mixins: [geoMixin],

  watch: {
    value(val) {
      this.innerShowValue = val;
    },

    innerShowValue(val) {
      this.$emit('input', val);
    },

    detail(val) {
      if (val) {
        this.provinces = [
          ...this.provinces,
          {
            id: val.provinceId,
            name: val.provinceName
          }
        ];
        this.cities = [
          ...this.cities,
          {
            id: val.cityId,
            name: val.cityName
          }
        ];
        this.subdistricts = [
          ...this.subdistricts,
          {
            id: val.subdistrictId,
            name: val.subdistrictName
          }
        ];
        this.villages = [
          ...this.villages,
          {
            id: val.villageId,
            name: val.villageName
          }
        ];
        this.form = {
          id: val.id,
          unitTypeId: val.unitTypeId,
          name: val.name,
          address: val.address,
          province: {
            id: val.provinceId,
            name: val.provinceName
          },
          city: {
            id: val.cityId,
            name: val.cityName
          },
          subdistrict: {
            id: val.subdistrictId,
            name: val.subdistrictName
          },
          district: {
            id: val.villageId,
            name: val.villageName
          },
          latitude: val.latitude,
          longitude: val.longitude,
          is_active: val.isActive,
          image: val.image,
          attributes: Object.entries(val.unitAttributes).map(
            ([key, value]) => ({
              attributeKey: key,
              attributeValue: value
            })
          )
        };
      }
    }
  },

  data() {
    return {
      formAddressName: 'form',
      innerShowValue: false,
      errorMessage: '',
      status: 'idle',
      formErrors: {},
      error: {
        address: ''
      },
      form: {
        id: '',
        unitTypeId: 1,
        name: null,
        address: null,
        province: null,
        city: null,
        subdistrict: null,
        district: null,
        latitude: null,
        longitude: null,
        is_active: true,
        image: null,
        attributes: []
      },
      dialog: {
        map: false
      }
    };
  },

  methods: {
    async handleSubmit() {
      const isValid = await this.$refs.observer.validate();
      const addressValid = this.validateAddress();
      if (isValid && addressValid) {
        try {
          if (this.form.id) {
            await this.handleUpdate(this.form, this.detail.id);
          } else {
            await this.handleCreate(this.form);
          }
          this.resetForm();
          this.$emit('submitted');
        } catch (e) {
          if (e?.response?.data) {
            const errorMessage = e.response.data.errors[0].message;
            this.errorMessage = errorMessage;
            this.formErrors = e.response.data.errors;
          }
        }
      }
    },

    addNewAttributes() {
      this.form.attributes.push({
        attributeKey: '',
        attributeValue: ''
      });
    },

    removeAttributes(index) {
      this.form.attributes.splice(index, 1);
    },

    setLocationFromMap(data) {
      this.form.latitude = data.latitude;
      this.form.longitude = data.longitude;
      // eslint-disable-next-line no-undef
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode(
        {
          latLng: {
            lat: data.latitude,
            lng: data.longitude
          }
        },
        (result, status) => {
          // eslint-disable-next-line no-undef
          if (status === google.maps.GeocoderStatus.OK) {
            this.form.address = result[0].formatted_address;
          }
        }
      );
      this.error.address = '';
    },

    async handleCreate(data) {
      this.status = 'pending';
      const payload = {
        villageId: data.district.id,
        unitTypeId: data.unitTypeId,
        name: data.name,
        address: data.address,
        latitude: data.latitude,
        longitude: data.longitude,
        isActive: data.is_active,
        image: data.image,
        attributes: data.attributes
      };
      try {
        await axios.post(
          `https://api-v2-monsta-mls.monsta.id/api/units`,
          payload,
          {
            headers: {
              Authorization:
                'Bearer oat_NQ.LVRzS0hkVTg1OEZXZjh6d1A2S0R0N3hTQ0FKZU9WVnEyaEJERVc4cTE0NDY3NDM0MzA'
            }
          }
        );
        this.status = 'resolved';
      } catch (error) {
        this.status = 'rejected';
        throw error;
      }
    },

    async handleUpdate(data) {
      this.status = 'pending';
      const payload = {
        villageId: data.district.id,
        unitTypeId: data.unitTypeId,
        name: data.name,
        address: data.address,
        latitude: data.latitude,
        longitude: data.longitude,
        isActive: data.is_active,
        image: data.image,
        attributes: data.attributes
      };
      try {
        await axios.put(
          `https://api-v2-monsta-mls.monsta.id/api/units/${data.id}`,
          payload,
          {
            headers: {
              Authorization:
                'Bearer oat_NQ.LVRzS0hkVTg1OEZXZjh6d1A2S0R0N3hTQ0FKZU9WVnEyaEJERVc4cTE0NDY3NDM0MzA'
            }
          }
        );
        this.status = 'resolved';
      } catch (error) {
        this.status = 'rejected';
        throw error;
      }
    },

    resetForm() {
      if (this.$refs.observer) this.$refs.observer.reset();
      this.innerShowValue = false;
      this.errorMessage = '';
      this.status = 'idle';
      this.formErrors = {};
      this.error = {
        address: ''
      };
      this.form = {
        id: '',
        unitTypeId: 1,
        name: null,
        address: null,
        province: null,
        city: null,
        subdistrict: null,
        district: null,
        latitude: null,
        longitude: null,
        is_active: true,
        image: null,
        attributes: []
      };
    },

    handleClose() {
      this.resetForm();
      this.innerShowValue = false;
    },

    setPlace(place) {
      this.center = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng()
      };
      this.form.latitude = place.geometry.location.lat();
      this.form.longitude = place.geometry.location.lng();
      this.form.address = place.formatted_address;
      this.error.address = '';
    },

    validateAddress(e) {
      if (e && e.type === 'change') {
        this.form.address = '';
        this.error.address = true;
        return false;
      }
      if (!this.form.address) {
        this.error.address = true;
        return false;
      }
      this.error.address = '';
      return true;
    }
  }
};
</script>
